import type { DraftFields } from './useCounterDraft'

/**
 * Held carts — the counter's "Hold" button (F-CTRL-N72 / F-FE-N158).
 *
 * A guest is still deciding and the next guest is ready: the cashier parks the
 * un-placed cart on the server and serves the next one. Any till can resume it.
 *
 * The snapshot is the same shape the local draft autosave writes (DraftFields):
 * product ids, quantities, option ids, notes and guest details — never a price.
 * Resuming runs it through the same restore path as a draft, so a held cart is
 * always re-priced against the live menu.
 *
 * Deliberately NOT an order: no order number, kitchen ticket, stock, payment or
 * fiscal row until the resumed cart is placed through the normal checkout.
 */

export interface HeldCart {
  id: string
  label: string
  order_type: string
  snapshot: DraftFields
  item_count: number
  /** Computed at hold time from that moment's prices. Display only — "est." */
  estimated_total: number
  customer_name?: string
  status: 'held' | 'resumed' | 'discarded' | 'expired'
  held_by?: string
  held_by_name?: string
  held_at: string
}

export interface CreateHeldCartRequest {
  label: string
  order_type: string
  snapshot: DraftFields
  item_count: number
  estimated_total: number
  customer_name?: string
}

/** Past this age a card turns amber: the guest has probably been forgotten. */
export const HELD_CART_STALE_MINUTES = 20
/** Soft warning threshold. The server refuses outright at 50. */
export const HELD_CART_SOFT_LIMIT = 25

/**
 * The automatic label. The guest's name when the cashier took one — that is
 * what they will call out — otherwise the order type and the time it was held.
 */
export function buildHeldCartLabel(
  orderTypeLabel: string,
  customerName: string,
  now: Date,
): string {
  const name = customerName.trim().replace(/\s+/g, ' ')
  const type = orderTypeLabel.trim() || 'Order'
  if (name) return `${name} · ${type}`.slice(0, 80)
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${type} · ${time}`.slice(0, 80)
}

export type HoldBlockReason = 'empty' | 'placed_order' | 'offline'

/**
 * Whether the current rail can be put on hold, and if not, why.
 *
 * Only a NEW, un-placed cart can be held (v1 scope, owner decision 2026-09-17).
 * Unsent items on an order that already exists — server-side or captured
 * offline — cannot: that order lives in the Open Orders strip and could be
 * paid, moved or voided elsewhere while the items sat on hold.
 */
export function holdBlockReason(input: {
  cartLength: number
  hasPlacedOrder: boolean
  online: boolean
}): HoldBlockReason | null {
  if (input.cartLength === 0) return 'empty'
  if (input.hasPlacedOrder) return 'placed_order'
  if (!input.online) return 'offline'
  return null
}

export function holdBlockMessage(reason: HoldBlockReason | null): string | null {
  switch (reason) {
    case 'placed_order':
      return 'Hold is for new orders. This order is already open (e.g. a table tab) — fire the new items or discard them.'
    case 'offline':
      return 'Hold needs internet — it will come back when you reconnect.'
    case 'empty':
    case null:
      return null
  }
}

/** Cashier-facing copy for the server's stable error codes. */
export function heldCartErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'held_cart_already_closed':
      return 'Another till already opened or discarded this held order.'
    case 'held_cart_not_found':
      return 'This held order no longer exists.'
    case 'held_cart_limit_reached':
      return 'Too many orders on hold — resume or discard some first.'
    case 'held_cart_empty':
      return 'There are no items to hold.'
    default:
      return fallback
  }
}

/** Whole minutes since the cart was held (never negative). */
export function heldCartAgeMinutes(heldAt: string, now: number): number {
  const t = Date.parse(heldAt)
  if (!Number.isFinite(t)) return 0
  return Math.max(0, Math.floor((now - t) / 60_000))
}

export function formatHeldCartAge(minutes: number): string {
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h} h` : `${h} h ${m} min`
}
